/*
 * Carbisdale - DictationAudio
 *
 * Kapselt Mikrofon-Zugriff und AudioWorklet-Setup.
 * Liefert PCM-Audio-Chunks via Callback.
 */

export class DictationAudio {
    constructor() {
        this._stream = null
        this._audioContext = null
        this._workletNode = null
        this._sourceNode = null

        /** @type {function(ArrayBuffer): void|null} */
        this.onAudioData = null
    }

    /**
     * Startet Audio-Aufnahme: Mikrofon oeffnen, AudioWorklet laden.
     * @throws {Error} wenn getUserMedia fehlschlaegt oder AudioWorklet nicht unterstuetzt
     */
    async start() {
        // AudioContext erstellen (Browser-Samplerate, typisch 48kHz)
        this._audioContext = new AudioContext()

        // AudioWorklet-Processor laden
        await this._audioContext.audioWorklet.addModule('js/dictation/DictationWorklet.js')

        // Mikrofon oeffnen
        this._stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        })

        // Audio-Pipeline aufbauen: Mikrofon → WorkletNode
        this._sourceNode = this._audioContext.createMediaStreamSource(this._stream)
        this._workletNode = new AudioWorkletNode(this._audioContext, 'dictation-worklet')

        // PCM-Chunks vom Worklet empfangen
        this._workletNode.port.onmessage = (event) => {
            if (this.onAudioData) {
                this.onAudioData(event.data)
            }
        }

        // Verbinden (kein Output noetig — Worklet sendet via port)
        this._sourceNode.connect(this._workletNode)
        // WorkletNode muss an destination connected sein, damit process() aufgerufen wird
        this._workletNode.connect(this._audioContext.destination)
    }

    /**
     * Stoppt Audio-Aufnahme und gibt alle Ressourcen frei.
     */
    stop() {
        // Worklet trennen
        if (this._workletNode) {
            this._workletNode.disconnect()
            this._workletNode.port.onmessage = null
            this._workletNode = null
        }

        // Source trennen
        if (this._sourceNode) {
            this._sourceNode.disconnect()
            this._sourceNode = null
        }

        // Mikrofon-Tracks stoppen
        if (this._stream) {
            for (const track of this._stream.getTracks()) {
                track.stop()
            }
            this._stream = null
        }

        // AudioContext schliessen
        if (this._audioContext) {
            this._audioContext.close().catch(() => {})
            this._audioContext = null
        }
    }

    /**
     * Prueft ob Audio-APIs im Browser verfuegbar sind.
     * @returns {boolean}
     */
    static isSupported() {
        return !!(
            typeof AudioContext !== 'undefined' &&
            typeof AudioWorkletNode !== 'undefined' &&
            navigator.mediaDevices?.getUserMedia
        )
    }
}
